<template>
    <bm-unit-data-headers
        ref="headersRef"
        :plantInfo="{ [plantBmuId]: { plantInfo } }"
        :initial-details-tab="initialDetailsTab"
        v-model:active-details-tab="activeDetailsTab"
        v-model:active-sub-details-tab="activeSubDetailsTab"
        v-model:chart-category="charts"
    />
    <bm-unit-data-visual
        :plant-bmu-id="plantBmuId"
        :plant-info="plantInfo"
        :date-override="dateOverride"
        :chart-date-range="chartDateRange"
        :active-details-tab="activeDetailsTab"
        :active-sub-details-tab="activeSubDetailsTab"
        :charts="charts"
        :multiView="false"
        :collision-padding-top="collisionPaddingTop"
    />
</template>

<script setup lang="ts">
import {
    onBeforeUnmount, onMounted, ref, watch,
} from 'vue';
import { IPlantDetails } from '@/interfaces/powerMarkets/plant/IPlantDetails.ts';
import { IDashboardOverrides } from '@/interfaces/powerMarkets/dashboard/IDashboard.ts';
import { IDateRange } from '@/interfaces/general/IDateRange.ts';
import type { PlantDetailsTab } from '@/constants/powerMarkets/plantDetailsTabs.ts';
import BmUnitDataHeaders from './BmUnitDataHeaders.vue';
import BmUnitDataVisual from './BmUnitDataVisual.vue';

const {
    plantBmuId,
    plantInfo,
    dateOverride,
    chartDateRange,
    initialDetailsTab,
} = defineProps<{
    plantBmuId: string;
    plantInfo: IPlantDetails;
    dateOverride?: IDashboardOverrides;
    chartDateRange?: IDateRange;
    initialDetailsTab?: PlantDetailsTab;
}>();

const activeDetailsTab = ref<PlantDetailsTab | ''>(initialDetailsTab ?? '');
const activeSubDetailsTab = ref<string>('');
const charts = ref<string[]>([]);
const collisionPaddingTop = ref<number>(0);
const headersRef = ref<InstanceType<typeof BmUnitDataHeaders> | null>(null);

let tabsResizeObserver: ResizeObserver | null = null;

function getBmUnitDetailsContainer (): HTMLElement | null {
    const headerElement = headersRef.value?.$el;
    if (!(headerElement instanceof HTMLElement)) return null;

    return headerElement.closest('.bm-unit-details');
}

function measureCollisionPaddingTop (): number {
    const container = getBmUnitDetailsContainer();
    if (!container) return 0;

    let totalHeight = 0;

    container.querySelectorAll('.tabs').forEach((tabElement) => {
        if (!(tabElement instanceof HTMLElement)) return;
        if (window.getComputedStyle(tabElement).position !== 'sticky') return;

        totalHeight += tabElement.getBoundingClientRect().height;
    });

    return totalHeight > 0 ? totalHeight + 60 : 0;
}

function updateCollisionPaddingTop (): void {
    collisionPaddingTop.value = measureCollisionPaddingTop();
}

function observeTabsHeight (): void {
    tabsResizeObserver?.disconnect();

    const container = getBmUnitDetailsContainer();
    if (!container) return;

    tabsResizeObserver = new ResizeObserver(() => {
        updateCollisionPaddingTop();
    });

    tabsResizeObserver.observe(container);
}

onMounted(() => {
    updateCollisionPaddingTop();
    observeTabsHeight();
});

onBeforeUnmount(() => {
    tabsResizeObserver?.disconnect();
    tabsResizeObserver = null;
});

watch(() => activeDetailsTab.value, () => {
    updateCollisionPaddingTop();
});

watch(() => activeSubDetailsTab.value, () => {
    updateCollisionPaddingTop();
});

watch(() => charts.value, () => {
    updateCollisionPaddingTop();
}, { deep: true });

watch(() => plantBmuId, () => {
    updateCollisionPaddingTop();
    observeTabsHeight();
});

</script>
